import { Switch, Route, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Toaster } from "@/components/ui/toaster";
import { useEffect, lazy, Suspense, Component, type ReactNode } from "react";
import { queryClient } from "@/lib/queryClient";
import { LocationProvider } from "@/contexts/LocationContext";
import { PageTransitionProvider } from "@/contexts/PageTransitionContext";

// ─── Eager imports ────────────────────────────────────────────────────────
// Only NotFound stays eager — it's tiny (~50 lines, no heavy deps) and is the
// final fallback that must render even if every chunk fails to fetch.
//
// Home is now LAZY. The previous "keep Home eager so first paint is fast"
// reasoning is backwards in dev: Home pulls Header (drags InstantSearch +
// Firebase + auth), LearningOpportunityCard (drags VideoCard + Radix), Hero
// (drags ProfessionalCarousel), Footer, etc. — ~30 modules total. Through the
// Replit dev tunnel (~3.5s per request), that's a minute of waterfall. Lazy
// Home means 1 extra round-trip for the chunk itself, but cuts the eager
// graph for EVERY non-home route from 46 source files down to ~15.
import NotFound from "@/pages/not-found";

const Home = lazy(() => import("@/pages/home"));

// Error boundary that auto-reloads when a lazy chunk fails to fetch.
// This happens routinely in dev when Vite restarts: the open tab holds
// stale chunk URLs that no longer resolve. A single hard reload fixes it.
// A sessionStorage guard prevents reload loops if the failure is persistent.
function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message || err || "");
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed")
  );
}

class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; isChunk: boolean }
> {
  state = { hasError: false, isChunk: false };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, isChunk: isChunkLoadError(error) };
  }
  componentDidCatch(error: Error) {
    console.debug("[ChunkErrorBoundary]", String(error?.message || error));
    if (isChunkLoadError(error) && typeof window !== "undefined") {
      const KEY = "tb_chunk_reload_at";
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }
  render() {
    if (this.state.hasError) {
      const label = this.state.isChunk
        ? "Reloading to recover from a stale page resource…"
        : "Something went wrong loading this page.";
      return (
        <div className="h-screen flex items-center justify-center bg-tb-cream">
          <div className="text-center px-6">
            <p className="text-tb-dark mb-6 max-w-prose text-pretty">{label}</p>
            <button
              className="px-6 py-2 rounded-full bg-tb-sage text-white"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Redirect component that properly uses hooks
function Redirect({ to }: { to: string }) {
  const [, setLocation] = useLocation();

  useEffect(() => {
    setLocation(to);
  }, [to, setLocation]);

  return null;
}

// ─── Lazy-loaded route components ─────────────────────────────────────────
// Everything below is code-split into per-page chunks. This dramatically
// reduces App.tsx's import fan-out from 100+ parallel requests to ~5,
// eliminating the Vite startup-race 404s and improving cold-start time.

// Onboarding / auth
const GetStarted = lazy(() => import("@/pages/get-started/sign-in"));
const NewOnboardingFlow = lazy(() => import("@/pages/onboarding/new-onboarding-flow"));
const DashboardV2Page = lazy(() => import("@/pages/portal/dashboard-v2"));
const TaskPage = lazy(() => import("@/pages/portal/task"));
// Kept, not part of task #11's cut: DashboardV2's own header dropdown links
// to these 2 (Billing manages the real $59 Stripe account) — no replacement
// exists in the new dashboard, so unlike the rest of Group B these stay live.
// No PortalLayout/PortalSidebar wrapper — both restyled to the dark,
// no-sidebar task.tsx pattern instead (founder call, 2026-08-21).
const PortalSettings = lazy(() => import("@/pages/portal/settings-new"));
const PortalBilling = lazy(() => import("@/pages/portal/billing"));
// PLAN.md §4 row #93 — new dedicated route (product-director's Q1 decision,
// row #90), not a DashboardV2 section. Same lazy-import pattern as its two
// /portal/* siblings above.
const PortalBoost = lazy(() => import("@/pages/portal/boost"));
// PLAN.md row 114 Stage D — real multi-class manager, replacing the old
// hard 2-slot first-class/second-class flow. Same dedicated-route/lazy-
// import pattern as its /portal/boost sibling directly above.
const PortalClasses = lazy(() => import("@/pages/portal/classes"));
// PLAN.md §4 row 104 — admin-only listing-page bulk-import tool. New route,
// deliberately NOT `/portal/admin/claimable-profiles` (that path is one of
// the retired old-admin-backend routes redirected to `/portal` below, task
// #11). Same lazy-import + dark-shell pattern as its two /portal/* siblings
// above; real access control is server-side (`verifyOnboardingManagerAccess`
// on both `/api/admin/bulk-import/*` routes).
const AdminBulkImport = lazy(() => import("@/pages/portal/bulk-import"));
const Docs = lazy(() => import("@/pages/Docs"));
const SubjectsIndex = lazy(() => import("@/pages/subjects"));
// /search is server-rendered for real (server/ssr/searchPage.ts, real data,
// crawlable) — this client component only exists so a JS-executing visitor
// who lands here post-hydration gets redirected to the equivalent real
// filter on / instead of a 404. See search-redirect.tsx for the full
// reasoning (why a redirect, not a second results page).
const SearchRedirect = lazy(() => import("@/pages/search-redirect"));
// "Paste your URL, see your rank" reveal (plan: scalable-frolicking-curry.md,
// PR2) — the hero's "See My Rank" CTA target for a URL-shaped paste. Own
// page, not a modal (founder call, 2026-08-23: a full profile preview inside
// the dialog broke its layout).
const SeeMyRank = lazy(() => import("@/pages/see-my-rank"));
// AI-readiness pivot plan, Phase 0: mock-data scaffold only, not yet linked
// from nav/sitemap — real data + SSR land in Phase B.
const LocationsStatePage = lazy(() => import("@/pages/locations/state"));
const SubjectCategoryPage = lazy(() => import("@/pages/subjects").then((m) => ({ default: m.SubjectCategoryPage })));
const SubjectDetailPage = lazy(() => import("@/pages/subjects").then((m) => ({ default: m.SubjectDetailPage })));
const Blog = lazy(() => import("@/pages/blog"));
const BlogPost = lazy(() => import("@/pages/blog/post"));
const ProfilePage = lazy(() => import("@/pages/profile-page"));

// Auth pages
const ResetPasswordPage = lazy(() => import("@/pages/reset-password"));
const VerifyEmailPage = lazy(() => import("@/pages/verify-email"));
const RoleSelection = lazy(() => import("@/pages/onboarding/role-selection"));
const NameStep = lazy(() => import("@/pages/onboarding/step1-name"));
const LearnerNameStep = lazy(() => import("@/pages/onboarding/learner-name"));
const Location = lazy(() => import("@/pages/get-started/location"));
const Profession = lazy(() => import("@/pages/get-started/profession"));
const Referral = lazy(() => import("@/pages/get-started/referral"));
const Upgrade = lazy(() => import("@/pages/get-started/upgrade"));
const PaywallPage = lazy(() => import("@/pages/paywall"));

const About = lazy(() => import("@/pages/about"));
const Contact = lazy(() => import("@/pages/contact"));
const Terms = lazy(() => import("@/pages/terms"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Subscribe = lazy(() => import("@/pages/subscribe"));
const SubscriptionUpgrade = lazy(() => import("@/pages/subscription-upgrade"));
const UpgradeSuccess = lazy(() => import("@/pages/upgrade-success"));
const PressMedia = lazy(() => import("@/pages/press-media"));
const Careers = lazy(() => import("@/pages/careers"));
const OurStory = lazy(() => import("@/pages/our-story"));
const Newsletter = lazy(() => import("@/pages/newsletter"));
const UnsubscribedPage = lazy(() => import("@/pages/unsubscribed"));
const LearnerDashboard = lazy(() => import("@/pages/learner-dashboard"));
const Teach = lazy(() => import("@/pages/teach"));
const Pricing = lazy(() => import("@/pages/pricing"));
const HowItWorks = lazy(() => import("@/pages/how-it-works"));
const ProfileExamples = lazy(() => import("@/pages/instructors"));
const Stats = lazy(() => import("@/pages/stats"));
const JoinPro = lazy(() => import("@/pages/join-pro"));
const InstructorNew = lazy(() => import("@/pages/instructor-new"));
const Signup = lazy(() => import("@/pages/start-for-free"));
const LearnerOnboarding = lazy(() => import("@/pages/onboarding/learner-onboarding"));
const SentryTest = lazy(() => import("@/pages/SentryTest"));
const OrganizationProfile = lazy(() => import("@/pages/organization-profile"));
const AcceptInvite = lazy(() => import("@/pages/accept-invite"));

// Booking pages
const BookingConfirmDetails = lazy(() => import("@/pages/booking-confirm-details"));
const BookingCheckout = lazy(() => import("@/pages/booking-checkout"));
const BookingConfirmation = lazy(() => import("@/pages/booking-confirmation"));

// Review submission (token-based, low-traffic)
const SubmitReview = lazy(() => import("@/pages/submit-review"));

// Portal "Your Page" — the single management interface (knowledge panel)

// Public marketing surfaces (single-product)
const DemoProfile = lazy(() => import("@/pages/demo-profile"));
import { SAMPLE_HANDLES } from "@/lib/panel/sampleHandles";

function Router() {
  // TIMING: Track when router/app starts loading after redirect
  const routerLoadStart = performance.now();

  // tasks.md #42 (found 2026-08-21, proven with real PageSpeed numbers
  // 2026-08-22): this used to `if (isLoading) return <spinner>` here, which
  // blocked EVERY route — including pure-public marketing/profile pages that
  // never touch auth — behind Firebase auth resolving. That's a real,
  // measured chunk of the 13.3s LCP: home.tsx's own chunk (11 KiB) couldn't
  // even start fetching until the ~930 KiB Firebase+Sentry chain finished.
  // Fix: don't gate render on isLoading at all. `useAuth()` already starts
  // `user` at `null` (isAuthenticated: false) until Firebase resolves, so
  // the ternary below naturally renders the public route set immediately;
  // once auth resolves, React re-renders into the authenticated set if
  // needed. Portal routes are unaffected — they're already unconditional
  // ("auth handled in components", see the comment further down) and were
  // never protected by this gate in the first place. The one real trade-off:
  // a genuinely logged-in user deep-linking straight to an authed-only route
  // (e.g. /subscribe) sees a brief NotFound flash until auth resolves and
  // the route re-appears, instead of a spinner — self-corrects in ~1s,
  // strictly better than every visitor waiting 10+s for content that was
  // never behind auth to begin with.
  const { user, isAuthenticated } = useAuth();
  const [location] = useLocation();

  // Scroll restoration - automatically scroll to top on route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <ChunkErrorBoundary>
    <Suspense
      fallback={
        <div className="h-screen flex items-center justify-center bg-tb-cream">
          <div className="animate-spin w-8 h-8 border-4 border-tb-sage border-t-transparent rounded-full" />
        </div>
      }
    >
    <Switch>
      {/* Retired standalone generator — redirect to the front door */}
      <Route path="/panel">{() => <Redirect to="/" />}</Route>
      <Route path="/panel/view">{() => <Redirect to="/" />}</Route>

      {/* Blog nav target (content currently lives under docs) */}
      <Route path="/blog" component={Blog} />
      <Route path="/blog/:slug" component={({ params }) => <BlogPost slug={params.slug} />} />

      {/* Single-product marketing surfaces */}
      <Route path="/for-instructors" component={() => <Redirect to="/" />} />
      <Route path="/how-it-works" component={HowItWorks} />
      <Route path="/instructors" component={ProfileExamples} />
      {/* Renamed from /profile-examples. The server 301s hard loads; this keeps
          in-app navigation from an old link working too. */}
      <Route path="/profile-examples">{() => <Redirect to="/instructors" />}</Route>
      {/* /instructors/:slug is the canonical instructor URL. A handful of
          illustrative sample pages share the namespace, so dispatch on the
          handle: samples render the demo panel, everything else is a real
          profile. (A Switch consumes the first match, so this cannot fall
          through to a later route — it must resolve both cases itself.) */}
      <Route path="/instructors/:profileSlug">
        {({ profileSlug }) =>
          SAMPLE_HANDLES.has(profileSlug!) ? (
            <DemoProfile handle={profileSlug!} />
          ) : (
            <ProfilePage profileSlug={profileSlug!} />
          )
        }
      </Route>
      {/* Legacy /demo/:handle URLs keep working. */}
      <Route path="/demo/:handle">{({ handle }) => <Redirect to={`/instructors/${handle}`} />}</Route>

      {/* Email verification route */}
      <Route path="/verify-email" component={VerifyEmailPage} />
      
      {/* Sentry test route (no-index, no-follow) */}
      <Route path="/sentry-test" component={SentryTest} />
      
      {/* Onboarding flow routes - EMAIL VERIFICATION REQUIRED */}
      <Route path="/join/role" component={RoleSelection} />
      <Route path="/join/name" component={NameStep} />
      <Route path="/join/learner/name" component={LearnerNameStep} />
      <Route path="/join/learner" component={LearnerOnboarding} />
      <Route path="/join/instructor" component={NewOnboardingFlow} />
      <Route path="/join/location" component={Location} />
      <Route path="/join/profession" component={Profession} />
      <Route path="/join/referral" component={Referral} />
      <Route path="/join/onboarding" component={NewOnboardingFlow} />
      <Route path="/join/pro" component={JoinPro} />
      
      {/* Get-started routes (alias for join routes) - EMAIL VERIFICATION REQUIRED */}
      <Route path="/get-started/location" component={Location} />
      <Route path="/get-started/profession" component={Profession} />
      <Route path="/get-started/referral" component={Referral} />
      
      {/* Get-started upgrade flow */}
      <Route path="/get-started/upgrade" component={Upgrade} />
      
      {/* Paywall route - shown after onboarding completion */}
      <Route path="/paywall" component={PaywallPage} />
      
      {/* Booking checkout flow */}
      <Route path="/booking/confirm" component={({ params }) => {
        const urlParams = new URLSearchParams(window.location.search);
        return <BookingConfirmDetails sessionId={urlParams.get('sessionId') || ''} listingId={urlParams.get('listingId') || ''} />;
      }} />
      <Route path="/booking/checkout" component={BookingCheckout} />
      <Route path="/booking/confirmation" component={BookingConfirmation} />
      
      {/* Portal routes - ALWAYS AVAILABLE (auth handled in components) */}
      {/* Cutover DONE (tasks.md "Portal v2" task #10, 2026-08-21): Portal v2's
          dashboard is now the real /portal home. Deliberately NOT wrapped in
          InstructorShell — the new design has its own dark header, no
          sidebar. /portal/dashboard-v2 kept as a redirect for old bookmarks. */}
      <Route path="/portal">
        {() => <DashboardV2Page />}
      </Route>
      <Route path="/portal/dashboard-v2">
        {() => <Redirect to="/portal" />}
      </Route>
      {/* One real, bookmarkable URL per Recommended Action (tasks.md #3a,
          revised 2026-08-21) — not a modal/overlay on the dashboard. Same
          dark shell as dashboard-v2, no InstructorShell. */}
      <Route path="/portal/tasks/:actionId">
        {({ actionId }) => <TaskPage actionId={actionId} />}
      </Route>
      {/* Old-portal-chrome audit (tasks.md "Portal v2" task #11, 2026-08-21):
          founder call — "none of this old ui should exist... we're only
          using the newly created dashboard." Reviews/Leads/Listings
          (InstructorShell/ManagePageShell, Group A) retired; every retired
          path redirects to the new dashboard instead of 404ing.
          `/portal/classes` was here too until PLAN.md row 114 Stage D gave
          it a real destination again (the multi-class manager registered
          below) — it must stay OUT of this list, or `Switch` matches this
          redirect first and the real route below becomes unreachable. */}
      {[
        "/portal/listings",
        "/portal/courses",
        "/portal/home",
        "/portal/messages",
        "/portal/reviews",
        "/portal/instructor/opportunities",
        "/portal/leads",
        "/portal/instructor/leads",
        "/portal/instructor/reviews",
        "/portal/listings/create",
        "/portal/courses/create",
      ].map((path) => (
        <Route key={path} path={path}>
          {() => <Redirect to="/portal" />}
        </Route>
      ))}
      {/* Old profile-editing routes retired (tasks.md "Portal v2" task #10,
          2026-08-21) — every field now lives at /portal/tasks/:actionId,
          reached from the dashboard's Recommended Actions. Redirect any
          stale bookmarks/links to the new hub instead of 404ing. */}
      {[
        "/portal/profile",
        "/portal/profile/edit",
        "/portal/profile/name",
        "/portal/profile/locations",
        "/portal/profile/bio",
        "/portal/profile/credentials",
        "/portal/profile/expertise",
        "/portal/profile/photos",
        "/portal/profile/videos",
        "/portal/profile/links",
      ].map((path) => (
        <Route key={path} path={path}>
          {() => <Redirect to="/portal" />}
        </Route>
      ))}
      {/* Missed in task #10's original cleanup — this route also rendered
          the now-deleted PortalProfilePage (undefined-identifier crash,
          same class as the InstructorSidebar Receipt bug from the same
          task, caught during the follow-up chrome audit). */}
      <Route path="/portal/instructor/profile">
        {() => <Redirect to="/portal" />}
      </Route>
      {/* Dynamic-param retired routes (task #11, Group A) — param dropped,
          destination is the same dashboard for all of them. */}
      <Route path="/portal/listings/edit/:courseId">{() => <Redirect to="/portal" />}</Route>
      <Route path="/portal/listings/stats/:listingId">{() => <Redirect to="/portal" />}</Route>
      <Route path="/portal/courses/edit/:courseId">{() => <Redirect to="/portal" />}</Route>
      <Route path="/portal/courses/stats/:listingId">{() => <Redirect to="/portal" />}</Route>
      <Route path="/portal/action/:actionId">{() => <Redirect to="/portal" />}</Route>
      <Route path="/portal/admin/instructors/:id">{() => <Redirect to="/portal" />}</Route>
      {/* Old-portal-chrome audit (task #11) — Groups B/C/D (Settings/Billing/
          Instructors/Venues/Sponsors/Waivers/Policies/Organization, the full
          admin backend, and the learner portal), all PortalLayout-wrapped
          light-theme chrome, retired per founder: "none of this old ui
          should exist... we're only using the newly created dashboard."
          Settings/Billing survive (DashboardV2's own header dropdown links
          here) but no longer use PortalLayout/PortalSidebar — restyled to
          the same dark, no-sidebar pattern as task.tsx (founder call,
          same day, while re-verifying this exact cutover live). */}
      <Route path="/portal/settings">
        {() => <PortalSettings />}
      </Route>
      <Route path="/portal/billing">
        {() => <PortalBilling />}
      </Route>
      {/* PLAN.md §4 row #93 — no redirect needed for old bookmarks, the path
          is new (row #91's own note). */}
      <Route path="/portal/boost">
        {() => <PortalBoost />}
      </Route>
      {/* PLAN.md row 114 Stage D — no redirect needed for old bookmarks,
          the path is new (same precedent as /portal/boost above). */}
      <Route path="/portal/classes">
        {() => <PortalClasses />}
      </Route>
      <Route path="/portal/admin/bulk-import">
        {() => <AdminBulkImport />}
      </Route>
      {[
        "/portal/instructors",
        "/portal/venues",
        "/portal/sponsors",
        "/portal/waivers",
        "/portal/policies",
        "/portal/organization",
        "/portal/admin/docs",
        "/portal/admin/subject-manager",
        "/portal/admin/city-manager",
        "/portal/admin/redirect-manager",
        "/portal/admin/badge-verification",
        "/portal/admin/instructor-manager",
        "/portal/admin/claimable-profiles",
        "/portal/admin/templates",
        "/portal/admin/review-moderation",
        "/portal/admin/support-requests",
        "/portal/admin/metrics",
        "/portal/admin/organizations",
        "/portal/learner/enrolled",
        "/portal/learner/wishlist",
        "/portal/learner/preferences",
        "/portal/learner/my-cities",
      ].map((path) => (
        <Route key={path} path={path}>
          {() => <Redirect to="/portal" />}
        </Route>
      ))}

      <Route path="/stats" component={Stats} />

      {/* Subjects directory - available for all users */}
      <Route path="/subjects" component={SubjectsIndex} />

      {/* /search — real data is server-rendered (server/ssr/searchPage.ts);
          this client route only redirects a JS visitor to the equivalent
          real filter on / (see search-redirect.tsx). */}
      <Route path="/search" component={SearchRedirect} />
      <Route path="/see-my-rank" component={SeeMyRank} />
      <Route path="/subjects/:categorySlug" component={({ params }) => <SubjectCategoryPage categorySlug={params.categorySlug} />} />
      <Route path="/subjects/:categorySlug/:subjectSlug" component={({ params }) => <SubjectDetailPage categorySlug={params.categorySlug} subjectSlug={params.subjectSlug} />} />

      {/* AI-readiness pivot plan, Phase 0: mock scaffold, not yet linked from
          nav/sitemap. Real SSR + data land in Phase B. */}
      <Route path="/locations/:stateSlug" component={({ params }) => <LocationsStatePage stateSlug={params.stateSlug} />} />

      {/* Public Docs Page - available for all users */}
      <Route path="/docs/:categorySlug/:sectionSlug/:pageSlug" component={Docs} />
      <Route path="/docs" component={Docs} />
      
      {/* Specific instructor routes MUST come before the profile slug wildcard */}
      <Route path="/instructor/new" component={InstructorNew} />

      {/* Profile viewing routes - available for all users */}
      <Route path="/pro/:profileSlug">{({ profileSlug }) => <Redirect to={`/instructors/${profileSlug}`} />}</Route>
      <Route path="/instructor/:profileSlug" component={({ params }) => <ProfilePage profileSlug={params.profileSlug} />} />
      
      {/* Review submission - available for all users (token-based access) */}
      <Route path="/submit-review" component={() => <SubmitReview />} />
      
      {/* Organization public profile */}
      <Route path="/org/:slug" component={({ params }) => <OrganizationProfile slug={params.slug} />} />
      
      {/* Accept organization invite */}
      <Route path="/accept-invite/:token" component={({ params }) => <AcceptInvite token={params.token} />} />

      {/* Auth routes - available for all users (needed for 2FA flow to complete) */}
      <Route path="/join" component={GetStarted} />
      <Route path="/signin" component={() => <Signup initialMode="signin" />} />
      <Route path="/sign-in" component={() => <Signup initialMode="signin" />} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/signup" component={() => <Signup />} />
      <Route path="/start-for-free" component={() => { window.location.replace('/signup'); return null; }} />
      
      {isAuthenticated ? (
        <>
          {/* Subscription routes for authenticated users */}
          <Route path="/subscribe" component={Subscribe} />
          <Route path="/subscription/upgrade" component={SubscriptionUpgrade} />
          <Route path="/upgrade-success" component={UpgradeSuccess} />

          {/* Learner Dashboard */}
          <Route path="/learner-dashboard" component={LearnerDashboard} />
          
          {/* Instructors directory - available for authenticated users */}
          
          {/* Teach / List Your Classes page + legacy redirects */}
          <Route path="/teach" component={Teach} />
          <Route path="/class-booking-event-registration" component={() => { window.location.replace('/teach'); return null; }} />
          <Route path="/lead-generation" component={() => { window.location.replace('/teach'); return null; }} />
          <Route path="/online-authority" component={() => { window.location.replace('/teach'); return null; }} />
          <Route path="/reputation-management" component={() => { window.location.replace('/teach'); return null; }} />
          
          {/* Newsletter - available for authenticated users */}
          <Route path="/newsletter" component={Newsletter} />
          
          

          
          
          {/* Redirect authenticated users to main portal */}
          <Route path="/" component={() => <Redirect to="/portal" />} />
          
          {/* All other routes for authenticated users - redirect to portal */}
          <Route component={() => <Redirect to="/portal" />} />
        </>
      ) : (
        <>
          {/* Public routes */}
          <Route path="/" component={Home} />

          
          
          
          {/* Topic pages */}
          
          {/* Specific instructor routes MUST come before the profile slug wildcard */}
          <Route path="/instructor/new" component={InstructorNew} />

          {/* Profile viewing routes */}
          <Route path="/pro/:profileSlug">{({ profileSlug }) => <Redirect to={`/instructors/${profileSlug}`} />}</Route>
          <Route path="/instructor/:profileSlug" component={({ params }) => <ProfilePage profileSlug={params.profileSlug} />} />
          
          
          {/* Instructors directory */}
          
          {/* Teach / List Your Classes page + legacy redirects */}
          <Route path="/teach" component={Teach} />
          <Route path="/class-booking-event-registration" component={() => { window.location.replace('/teach'); return null; }} />
          <Route path="/lead-generation" component={() => { window.location.replace('/teach'); return null; }} />
          <Route path="/online-authority" component={() => { window.location.replace('/teach'); return null; }} />
          <Route path="/reputation-management" component={() => { window.location.replace('/teach'); return null; }} />
          
          
          {/* Redirect old trending URLs */}
          
          {/* Redirect old trending skills URL */}
          
          <Route path="/pricing" component={Pricing} />
          <Route path="/for-instructors" component={() => <Redirect to="/" />} />
          
          {/* Review submission page - must be before /about to avoid conflicts */}
          <Route path="/review/:token" component={({ params }) => {
            return (
              <Suspense fallback={
                <div className="min-h-screen bg-tb-cream flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-6 max-w-prose text-sm text-gray-600 text-pretty">Loading review form...</p>
                  </div>
                </div>
              }>
                <SubmitReview token={params.token} />
              </Suspense>
            );
          }} />
          
          <Route path="/newsletter" component={Newsletter} />
          <Route path="/about" component={About} />
          <Route path="/contact-us" component={Contact} />
          <Route path="/contact" component={() => <Redirect to="/contact-us" />} />
          <Route path="/terms" component={Terms} />
          <Route path="/privacy" component={Privacy} />
          <Route path="/press" component={PressMedia} />
          <Route path="/careers" component={Careers} />
          <Route path="/our-story" component={OurStory} />
          <Route path="/help-center" component={() => <Redirect to="/docs" />} />
          <Route path="/support" component={() => <Redirect to="/docs" />} />
          <Route path="/newsletter" component={Newsletter} />
          <Route path="/unsubscribed" component={UnsubscribedPage} />
          <Route path="/faq" component={() => <Redirect to="/docs" />} />
        </>
      )}
      
      {/* Root-level city and subject routing - MUST BE LAST before NotFound to ensure static routes take precedence */}
      {/* TopicRouter will detect if it's a city or subject and route accordingly */}
      
      <Route component={NotFound} />
    </Switch>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LocationProvider>
        <PageTransitionProvider>
          <div className="min-h-screen bg-tb-cream">
            <Router />
            <Toaster />
          </div>
        </PageTransitionProvider>
      </LocationProvider>
    </QueryClientProvider>
  );
}